/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ProjectvariantSearchbar.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useState, useEffect, useRef, memo, useCallback, useMemo } from "react";
import {
  Box,
  TextField,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  InputAdornment,
  Paper,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { useDispatch, useSelector } from "react-redux";
import { HandleApiActions, projectConfigAPI } from "../../../../redux/actions";
import {
  selectProjectAction,
  selectVariantAction,
} from "../../../../redux/actions/projectConfigActions";

import PropTypes from "prop-types";

const SEARCH_TEXT_PATTERN = /^([^[]*)(?:\[(.*?)\])?$/;

const getProjectTextColor = (isSelected, isDisabled) => {
  if (isSelected) {
    return "#fff";
  }
  if (isDisabled) {
    return "#363535ff";
  }
  return "#000";
};

function ProjectVariantSearchbar({ displayProjectAlone = false }) {
  const dispatch = useDispatch();
  const inputRef = useRef(null);

  const rawProjects = useSelector((s) => s?.projectConfigReducer?.projects);
  const projects = useMemo(() => rawProjects || [], [rawProjects]);
  const rawVariants = useSelector((s) => s?.projectConfigReducer?.variants);
  const variantsFromRedux = useMemo(() => rawVariants || [], [rawVariants]);
  const selectedProjectDetails = useSelector(
    (s) => s?.projectConfigReducer?.selectedProjectDetails,
  );
  const selectedVariantDetails = useSelector(
    (s) => s?.projectConfigReducer?.selectedVariantDetails,
  );
  const selectedProjectId = useSelector(
    (s) => s?.projectConfigReducer?.selectedProjectId,
  );

  const [projectsData, setProjectsData] = useState([]); // [{ project_id, name, value, label }]
  const [variantsData, setVariantsData] = useState([]); // [{ variant_id, name, value, label }]
  const [searchText, setSearchText] = useState("");
  const [toolTipText, setToolTipText] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [showVariants, setShowVariants] = useState(false); // controls whether dropdown shows variants or projects
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [filteredVariants, setFilteredVariants] = useState([]);
  const containerRef = useRef(null);
  const autoSelectRef = useRef({ projectId: null });

  const getProjects = useCallback(async () => {
    const apiDetails = projectConfigAPI?.get_projects_main;
    await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...apiDetails,
          show_toast: false,
          params: {
            page_number: 1,
            page_size: 10000,
            order_by_column: "created_at", //@Changed by abdul
            order_by_sort: "DESC",
          },
        }),
      ),
    );
  }, [dispatch]);

  const getVariants = useCallback(async (projectId) => {
    if (!projectId) {
      setVariantsData([]);
      return;
    }

    const apiDetails = projectConfigAPI.get_variants_main;
    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...apiDetails,
          show_toast: false,
          params: {
            project_id: Number(projectId),
            is_complete_details_required: true,
            sort_column: "name",
            sort_order: "ASC",
          },
        }),
      ),
    );
    return resp?.data;
  }, [dispatch]);

  const truncate = (str, max = 8) => {
    if (!str) return "";
    return str.length > max ? str.substring(0, max) + "..." : str;
  };

  useEffect(() => {
    getProjects();
  }, [getProjects]);

  useEffect(() => {
    const mapped = (projects || []).map((p) => ({
      project_id: p.project_id,
      name: p.name,
      value: p.project_id,
      label: p.name,
    }));
    setProjectsData(mapped);
    setFilteredProjects(mapped);
  }, [projects]);

  useEffect(() => {
    if (displayProjectAlone) {
      setVariantsData([]);
      setFilteredVariants([]);
      setShowVariants(false);
      return;
    }

    if (!selectedProjectId || !showVariants) return;

    const currentProjectVariants = (variantsFromRedux || []).filter(
      (v) => Number(v.project_id) === Number(selectedProjectId),
    );

    const mapped = currentProjectVariants.map((v) => ({
      variant_id: v.variant_id,
      name: v.name,
      value: v.variant_id,
      label: v.name,
      project_id: v.project_id,
    }));
    setVariantsData(mapped);
    setFilteredVariants(mapped);
  }, [variantsFromRedux, selectedProjectId, showVariants, displayProjectAlone]);

  useEffect(() => {
    if (displayProjectAlone) {
      setVariantsData([]);
      setFilteredVariants([]);
      return;
    }

    if (selectedProjectId) {
      getVariants(selectedProjectId);
    } else {
      setVariantsData([]);
      setFilteredVariants([]);
    }
  }, [selectedProjectId, getVariants, displayProjectAlone]);

  useEffect(() => {
    if (displayProjectAlone) return;
    if (!selectedProjectId) return;
    const currentProjectVariants = (variantsFromRedux || []).filter(
      (v) => Number(v.project_id) === Number(selectedProjectId),
    );
    if (currentProjectVariants.length === 0) return;

    if (
      (autoSelectRef.current.projectId !== selectedProjectId ||
        autoSelectRef.current.projectId === null) &&
      !selectedVariantDetails
    ) {
      const first = currentProjectVariants[0];

      dispatch(selectVariantAction(first.variant_id));
      const projectName =
        selectedProjectDetails?.name ||
        projectsData.find(
          (p) => Number(p.project_id) === Number(selectedProjectId),
        )?.name ||
        "";
      setSearchText(
        projectName
          ? `${truncate(projectName)} [${truncate(first.name)}]`
          : `[${truncate(first.name)}]`,
      );
      setToolTipText(
        projectName ? `${projectName} [${first.name}]` : first.name,
      );
      autoSelectRef.current.projectId = selectedProjectId;
    }
  }, [
    variantsFromRedux,
    selectedProjectId,
    dispatch,
    selectedProjectDetails,
    projectsData,
    selectedVariantDetails,
    displayProjectAlone,
  ]);

  useEffect(() => {
    if (selectedProjectDetails?.name) {
      const projectName = selectedProjectDetails.name;
      const variantName = displayProjectAlone ? null : selectedVariantDetails?.name;
      setSearchText(
        variantName
          ? `${truncate(projectName)} [${truncate(variantName)}]`
          : truncate(projectName),
      );
      setToolTipText(
        variantName ? `${projectName} [${variantName}]` : projectName,
      );
    } else {
      setSearchText("");
      setToolTipText("");
    }
  }, [selectedProjectDetails, selectedVariantDetails, displayProjectAlone]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setExpanded(false);

        if (selectedProjectDetails) {
          const projectName = selectedProjectDetails.name || "";
          const variantName = displayProjectAlone ? null : selectedVariantDetails?.name;
          setSearchText(
            variantName
              ? `${truncate(projectName)} [${truncate(variantName)}]`
              : truncate(projectName),
          );
          setToolTipText(
            variantName ? `${projectName} [${variantName}]` : projectName,
          );
        }

        setShowVariants(!displayProjectAlone && !!selectedVariantDetails);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [selectedProjectDetails, selectedVariantDetails, displayProjectAlone]);

  useEffect(() => {
    const text = (searchText || "").trim();
    console.log("---selected project---", selectedProjectDetails);
    const match = SEARCH_TEXT_PATTERN.exec(text);
    const projectPart = (match?.[1] || "").trim().toLowerCase();
    const variantPart = (match?.[2] || "").trim().toLowerCase();
    const hasBracket = text.includes("[");

    const filterProjectsBy = (q) => {
      if (!q) return projectsData;
      const filtered = projectsData.filter((p) =>
        p.label.toLowerCase().includes(q),
      );
      console.log("filtered project", filtered);
      return filtered.length
        ? filtered
        : [
            {
              label: "Project not found",
              name: "Project not found",
              disabled: true,
            },
          ];
    };

    const matchedProjectExact = projectsData.find(
      (p) =>
        p.label.toLowerCase() === selectedProjectDetails?.name?.toLowerCase(),
    );

    console.log("---matchedProjectExact---", matchedProjectExact);

    if (displayProjectAlone) {
      setShowVariants(false);
      setFilteredProjects(filterProjectsBy(text.toLowerCase()));
      return;
    }

    if (hasBracket && variantPart.length === 0 && text.endsWith("[")) {
      setShowVariants(false);
      setFilteredProjects(filterProjectsBy(projectPart));
      return;
    }


    if (hasBracket) {
      if (matchedProjectExact && variantPart.length > 0) {
        setShowVariants(true);
        const baseVariants = variantsData.filter((v) =>
          v.project_id
            ? Number(v.project_id) === Number(matchedProjectExact.value)
            : true,
        );
        const filtered = baseVariants.filter((v) =>
          v.label.toLowerCase().includes(variantPart),
        );
        setFilteredVariants(filtered.length ? filtered : baseVariants);
        return;
      }

      setShowVariants(false);
      setFilteredProjects(filterProjectsBy(projectPart));
      return;
    }

    if (!text) {
      setShowVariants(false);
      setFilteredProjects(projectsData);
      return;
    }

    setShowVariants(false);
    setFilteredProjects(filterProjectsBy(projectPart));
  }, [searchText, projectsData, variantsData, selectedProjectDetails, displayProjectAlone]);

  const handleMouseDown = () => {
    setExpanded(true);
    const input = inputRef.current;
    const text = searchText.trim();
    if (!input) return;
    const hasVariant = text.includes("[") && text.includes("]");

    if (hasVariant) {
      setTimeout(() => {
        input.setSelectionRange(text.length, text.length);
      }, 0);
    }
  };

  const handleFocus = () => {
    setExpanded(true);
    const input = inputRef.current;
    const text = searchText.trim();

    if (!input) return;
    const hasVariant = text.includes("[") && text.includes("]");


    if (hasVariant) {
      setTimeout(() => {
        input.setSelectionRange(text.length, text.length);
      }, 0);
    }
    if (!text) {
      setShowVariants(false);
      setFilteredProjects(projectsData);
      return;
    }

    if (displayProjectAlone) {
      const filtered = projectsData.filter((p) =>
        p.label.toLowerCase().includes(text.toLowerCase()),
      );
      setShowVariants(false);
      setFilteredProjects(filtered.length ? filtered : projectsData);
      return;
    }

    const match = SEARCH_TEXT_PATTERN.exec(text);
    const projectPart = match?.[1]?.trim()?.toLowerCase() || "";
    const variantPart = match?.[2]?.trim()?.toLowerCase() || "";

    console.log("projectPart", projectPart, "variantPart", variantPart);

    const matchedProject = projectsData.find(
      (p) =>
        p.label.toLowerCase() === selectedProjectDetails?.name?.toLowerCase(),
    );

    const hasValidVariant = matchedProject && variantPart.length > 0;

    if (hasValidVariant) {
      setShowVariants(true);
      setFilteredVariants(variantsData);
      return;
    }

    const filtered = projectsData.filter((p) =>
      p.label.toLowerCase().includes(projectPart),
    );

    setShowVariants(false);
    setFilteredProjects(filtered.length ? filtered : projectsData);
  };

  const handleKeyDown = (e) => {
    if (e.key !== "Backspace") return;

    const input = inputRef.current;
    if (!input) return;

    const cursorPos = input.selectionStart;
    const value = searchText;

    const startBracket = value.indexOf("[");
    const endBracket = value.indexOf("]");

    const isCursorAtVariantEnd = cursorPos === endBracket + 1;

    const variantExists = startBracket !== -1 && endBracket !== -1;

    if (variantExists && isCursorAtVariantEnd) {
      e.preventDefault(); // stop default backspace behavior

      const newValue = selectedProjectDetails?.name;

      setSearchText(newValue);
      setToolTipText("");
      setShowVariants(false);

      setTimeout(() => {
        input.setSelectionRange(newValue.length, newValue.length);
      }, 0);
    }

  };

  const handleClear = () => {
    setSearchText("");
    setToolTipText("");
    setExpanded(true);
    setShowVariants(false); // show projects only on clear
    setFilteredProjects(projectsData || []);
  };

  const handleProjectClick = async (project) => {
    if (!project) return;

    autoSelectRef.current.projectId = null;

    if (displayProjectAlone) {
      dispatch(selectProjectAction(Number(project.value)));
      setSearchText(project.label);
      setToolTipText(project.label);
      setExpanded(false);
      setShowVariants(false);
      return;
    }

    if (Number(selectedProjectId) === Number(project.value)) {
      await getVariants(selectedProjectId);

      const currentProjectVariants = (variantsFromRedux || []).filter(
        (v) => Number(v.project_id) === Number(selectedProjectId),
      );
      const mapped = currentProjectVariants.map((v) => ({
        variant_id: v.variant_id,
        name: v.name,
        value: v.variant_id,
        label: v.name,
        project_id: v.project_id,
      }));
      setVariantsData(mapped);
      setFilteredVariants(mapped);
      setShowVariants(true);

      if (mapped.length > 0) {
        const first = mapped[0];
        dispatch(selectVariantAction(first.variant_id));
        setSearchText(`${truncate(project.label)} [${truncate(first.name)}]`);
        setToolTipText(`${project.label} [${first.name}]`);
      }
    } else {
      dispatch(selectProjectAction(Number(project.value)));
    }

    setSearchText(project.label);
    setToolTipText(project.label);
    setExpanded(false);
  };

  const handleVariantClick = (variant) => {
    console.log("---handleVariantClick---", variant);
    if (!variant) return;

    dispatch(selectVariantAction(variant.variant_id));
    const projectLabel =
      selectedProjectDetails?.name || searchText.split("[")[0]?.trim() || "";
    setSearchText(`${truncate(projectLabel)} [${truncate(variant.name)}]`);
    setToolTipText(`${projectLabel} [${variant.name}]`);
    setExpanded(false);
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        width: 300,
        fontFamily: "Arial",
        position: "relative",
        borderRadius: 0,
      }}
    >
      <Tooltip
        title={toolTipText}
        arrow
        placement="right"
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: "#000", // pure black tooltip
              color: "#fff",
              borderRadius: 0,
              boxShadow: 3,
              p: 1,
              "& .MuiTooltip-arrow": {
                color: "#000",
              },
            },
          },
        }}
      >
        <TextField
          fullWidth
          size="small"
          value={searchText}
          inputRef={inputRef}
          onKeyDown={handleKeyDown}
          placeholder={
            displayProjectAlone ? "Search project..." : "Search project or variant..."
          }
          onChange={(e) => {
            const text = e.target.value;
            if (text?.includes("]") || text?.includes("[")) {
              return;
            }
            setSearchText(text);
            setToolTipText("");
          }}
          onFocus={handleFocus}
          onMouseDown={handleMouseDown}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 0,
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "black",
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {searchText && (
                  <IconButton size="small" onClick={handleClear}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
        />
      </Tooltip>

      {expanded && (
        <Paper
          sx={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            maxHeight: 250,
            overflowY: "auto",
            border: "1px solid #ccc",
            mt: 0.5,
            zIndex: 10,
          }}
        >
          <List disablePadding>
            {!showVariants &&
              (filteredProjects || []).map((project) => (
                <ListItemButton
                  key={project.value}
                  onClick={() => {
                    if (!project.disabled) {
                      handleProjectClick(project);
                    }
                  }}
                  sx={{
                    bgcolor:
                      selectedProjectDetails?.project_id === project.project_id
                        ? "#000"
                        : "#fff",
                    color: getProjectTextColor(
                      selectedProjectDetails?.project_id === project.project_id,
                      project.disabled,
                    ),
                    "&:hover": {
                      bgcolor: project.disabled ? "#fff" : "#000",
                      color: project.disabled ? "#000" : "#fff",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  <ListItemText
                    primary={project.label}
                    primaryTypographyProps={{
                      textTransform: "capitalize",
                    }}
                  />
                </ListItemButton>
              ))}

            {showVariants &&
              (filteredVariants || []).map((variant) => (
                <ListItemButton
                  key={variant.value}
                  onClick={() => handleVariantClick(variant)}
                  sx={{
                    bgcolor:
                      selectedVariantDetails?.variant_id === variant.variant_id
                        ? "#000"
                        : "#fff",
                    color:
                      selectedVariantDetails?.variant_id === variant.variant_id
                        ? "#fff"
                        : "#000",
                    "&:hover": { bgcolor: "#000", color: "#fff" },
                    transition: "all 0.2s ease",
                    pl: 3,
                  }}
                >
                  <ListItemText
                    primary={variant.label}
                    primaryTypographyProps={{
                      textTransform: "capitalize",
                    }}
                  />
                </ListItemButton>
              ))}
          </List>
        </Paper>
      )}
    </Box>
  );
}

ProjectVariantSearchbar.propTypes = {
  displayProjectAlone: PropTypes.bool,
};

export default memo(ProjectVariantSearchbar);
